import React from 'react';
import { X } from 'lucide-react';
import Moment from 'react-moment';

const PaymentDetailsModal = ({ payment, isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative min-h-screen flex items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl max-w-3xl w-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b">
            <h2 className="text-xl font-semibold text-gray-900">Payment Details</h2>
            <button
              onClick={onClose}
              className="p-1 rounded-full hover:bg-gray-100 transition-colors"
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Basic Info Section */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Basic Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Payment ID</p>
                  <p className="text-sm font-medium text-gray-900">{payment.paymentId}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Payment Date</p>
                  <p className="text-sm font-medium text-gray-900">
                    {payment.paymentDate ? (
                      <Moment format="DD MMM YYYY hh:mm:ss A">
                        {payment.paymentDate}
                      </Moment>
                    ) : 'Not available'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Status</p>
                  <span
                    className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                      payment.status === 'SUCCESS'
                        ? 'bg-green-100 text-green-800'
                        : payment.status === 'PENDING'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-red-100 text-red-800'
                    }`}
                  >
                    {payment.status}
                  </span>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Amount</p>
                  <p className="text-sm font-medium text-gray-900">₹{payment.amount}</p>
                </div>
              </div>
            </div>

            {/* User Info Section */}
            <div className="space-y-4 pt-4 border-t">
              <h3 className="text-lg font-medium text-gray-900">User Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">User Name</p>
                  <p className="text-sm font-medium text-gray-900">{payment.userName}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Mobile Number</p>
                  <p className="text-sm font-medium text-gray-900">{payment.mobileNumber}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">User ID</p>
                  <p className="text-sm font-medium text-gray-900">{payment.userId}</p>
                </div>
              </div>
            </div>

            {/* Payment Method Section */}
            <div className="space-y-4 pt-4 border-t">
              <h3 className="text-lg font-medium text-gray-900">Payment Method Details</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Payment Method</p>
                  <p className="text-sm font-medium text-gray-900">{payment.paymentMethod}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Payment Type</p>
                  <p className="text-sm font-medium text-gray-900">{payment.paymentType}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Payment Via</p>
                  <p className="text-sm font-medium text-gray-900">{payment.paymentVia || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">UTR Number</p>
                  <p className="text-sm font-medium text-gray-900">{payment.utrNo || 'N/A'}</p>
                </div>
              </div>
            </div>

            {/* Additional Details Section */}
            <div className="space-y-4 pt-4 border-t">
              <h3 className="text-lg font-medium text-gray-900">Additional Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Subscription ID</p>
                  <p className="text-sm font-medium text-gray-900">{payment.subscriptionId || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Cashfree Order ID</p>
                  <p className="text-sm font-medium text-gray-900 break-all">{payment.cashfreeOrderId || 'N/A'}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-sm text-gray-500">Payment Session ID</p>
                  <p className="text-sm font-medium text-gray-900 break-all">{payment.paymentSessionId || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">CF Order ID</p>
                  <p className="text-sm font-medium text-gray-900">{payment.cfOrderId || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">CF Payment ID</p>
                  <p className="text-sm font-medium text-gray-900">{payment.cfPaymentId || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">CF Transaction ID</p>
                  <p className="text-sm font-medium text-gray-900">{payment.cfTransactionId || 'N/A'}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 p-4 border-t">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentDetailsModal;